
import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, Timer, X, RefreshCw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { usePolicyCompliance, PolicyAlert, PolicyAuditJobStatus } from '@/hooks/admin/usePolicyCompliance';
import { format } from 'date-fns';

export function PolicyComplianceDashboard() {
  const {
    loading,
    enforcementEnabled,
    enforcementStatus,
    auditExistingPolicies,
    getPolicyAuditJobStatus,
    getPolicyEnforcementStatus,
    fixPolicy,
    togglePolicyTrigger,
    updateAlertStatus
  } = usePolicyCompliance();

  const [alerts, setAlerts] = useState<PolicyAlert[]>([]);
  const [jobStatus, setJobStatus] = useState<PolicyAuditJobStatus | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('alerts');

  // Fetch policy alerts
  const fetchAlerts = useCallback(async () => {
    const data = await auditExistingPolicies();
    if (data) {
      setAlerts(data);
    }
  }, [auditExistingPolicies]);

  // Fetch job status
  const fetchJobStatus = useCallback(async () => {
    const data = await getPolicyAuditJobStatus();
    if (data) {
      setJobStatus(data);
    }
  }, [getPolicyAuditJobStatus]);

  // Fetch enforcement status
  const fetchEnforcementStatus = useCallback(async () => {
    await getPolicyEnforcementStatus();
  }, [getPolicyEnforcementStatus]);

  // Load data on component mount - using direct function calls to avoid dependency loop
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchAlerts(),
        fetchJobStatus(),
        fetchEnforcementStatus()
      ]);
    };

    loadData();
  }, [fetchAlerts, fetchJobStatus, fetchEnforcementStatus]); // Include all dependencies

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchAlerts(),
        fetchJobStatus(),
        fetchEnforcementStatus()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle enforcement toggle
  const handleToggleEnforcement = async () => {
    const newStatus = !enforcementEnabled;
    const success = await togglePolicyTrigger(newStatus);
    
    if (success) {
      await fetchEnforcementStatus();
    }
  };

  // Handle fix policy
  const handleFixPolicy = async (policyName: string, tableName: string, alertId: string) => {
    const success = await fixPolicy(policyName, tableName);
    
    if (success) {
      // Mark as resolved
      await updateAlertStatus(alertId, 'Resolved', 'Fixed automatically by the system');
      await fetchAlerts();
    }
  };

  // Handle update alert status
  const handleUpdateAlertStatus = async (alertId: string, status: 'Reviewing' | 'Resolved' | 'Ignored') => {
    const success = await updateAlertStatus(alertId, status);
    
    if (success) {
      await fetchAlerts();
    }
  };

  // Render severity badge
  const renderSeverityBadge = (severity: string) => {
    const color = severity === 'High' ? 'bg-red-500' : 
                 severity === 'Medium' ? 'bg-yellow-500' : 'bg-blue-500';
    
    return (
      <Badge className={`${color} text-white`}>
        {severity}
      </Badge>
    );
  };

  // Render status badge
  const renderStatusBadge = (status: string) => {
    const color = status === 'New' ? 'bg-red-500' : 
                 status === 'Reviewing' ? 'bg-yellow-500' : 
                 status === 'Resolved' ? 'bg-green-500' : 'bg-gray-500';
    
    return (
      <Badge className={`${color} text-white`}>
        {status}
      </Badge>
    );
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return format(new Date(dateStr), 'MMM d, yyyy HH:mm');
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Policy Compliance</h1>
          <p className="text-muted-foreground mt-2">
            Monitor and enforce admin check standards for Row Level Security policies
          </p>
          {alerts.length === 0 && jobStatus && !jobStatus.active && (
            <p className="text-sm text-yellow-600 mt-1">
              ⚠️ Policy compliance features are being configured
            </p>
          )}
        </div>
        
        <Button onClick={handleRefresh} disabled={refreshing || loading}>
          {refreshing || loading ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </>
          )}
        </Button>
      </div>
      
      <div className="grid gap-6 md:grid-cols-3 mb-8">
        {/* Policy Enforcement Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Policy Enforcement</CardTitle>
            <CardDescription>Control policy creation standards</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span>Enforcement Status:</span>
                <Badge variant={enforcementEnabled ? "default" : "outline"}>
                  {enforcementEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              
              <Switch 
                checked={!!enforcementEnabled} 
                onCheckedChange={handleToggleEnforcement}
                disabled={loading}
              />
            </div>
            
            {enforcementStatus && enforcementStatus.last_changed && (
              <p className="text-sm text-muted-foreground mt-2">
                Last changed: {formatDate(enforcementStatus.last_changed)}
              </p>
            )}
          </CardContent>
        </Card>
        
        {/* Job Status Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Audit Job</CardTitle>
            <CardDescription>Scheduled policy scanning</CardDescription>
          </CardHeader>
          <CardContent>
            {jobStatus ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant={jobStatus.active ? "default" : "outline"}>
                    {jobStatus.active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Schedule:</span>
                  <span>{jobStatus.schedule || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Run:</span>
                  <span>{jobStatus.last_run ? formatDate(jobStatus.last_run) : 'Never'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Next Run:</span>
                  <span>{jobStatus.next_run ? formatDate(jobStatus.next_run) : 'N/A'}</span>
                </div>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-24">
                <RefreshCw className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-4">
                No job status available
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Alerts Summary Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Alerts Summary</CardTitle>
            <CardDescription>Policy compliance status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Alerts:</span>
                <span>{alerts.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">New Issues:</span>
                <Badge variant="destructive">
                  {alerts.filter((alert: PolicyAlert) => alert.status === 'New').length}
                </Badge>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">In Review:</span>
                <Badge variant="outline" className="bg-yellow-100">
                  {alerts.filter((alert: PolicyAlert) => alert.status === 'Reviewing').length}
                </Badge>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Resolved:</span>
                <Badge variant="outline" className="bg-green-100">
                  {alerts.filter((alert: PolicyAlert) => alert.status === 'Resolved').length}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="alerts">Policy Alerts</TabsTrigger>
          <TabsTrigger value="documentation">Documentation</TabsTrigger>
        </TabsList>
        
        <TabsContent value="alerts" className="space-y-4">
          {alerts.length > 0 ? (
            alerts.map((alert: PolicyAlert) => (
              <Card key={alert.id} className="overflow-hidden">
                <CardHeader className="bg-muted/50 pb-2">
                  <div className="flex justify-between">
                    <CardTitle className="text-lg">{alert.policy_name}</CardTitle>
                    <div className="flex space-x-2">
                      {renderSeverityBadge(alert.severity)}
                      {renderStatusBadge(alert.status)}
                    </div>
                  </div>
                  <CardDescription>
                    Table: <code>{alert.table_name}</code> • Issue: {alert.issue_type}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pt-4">
                  <p className="mb-4">{alert.description}</p>
                  
                  <Alert className="bg-muted/50">
                    <AlertTitle className="text-xs font-mono">Policy Definition</AlertTitle>
                    <AlertDescription>
                      <pre className="text-xs overflow-x-auto p-2 bg-muted rounded">
                        {alert.policy_definition}
                      </pre>
                    </AlertDescription>
                  </Alert>
                  
                  {alert.notes && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold mb-1">Notes:</h4>
                      <p className="text-sm text-muted-foreground">{alert.notes}</p>
                    </div>
                  )}
                </CardContent>
                
                <CardFooter className="flex justify-between bg-muted/20 border-t">
                  <div className="text-sm text-muted-foreground">
                    Created: {formatDate(alert.created_at)}
                    {alert.resolved_at && ` • Resolved: ${formatDate(alert.resolved_at)}`}
                  </div>
                  
                  <div className="flex space-x-2">
                    {alert.status === 'New' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleUpdateAlertStatus(alert.id, 'Reviewing')}
                          disabled={loading}
                        >
                          <Timer className="mr-1 h-4 w-4" />
                          Review
                        </Button>
                        
                        <Button 
                          size="sm"
                          onClick={() => handleFixPolicy(alert.policy_name, alert.table_name, alert.id)}
                          disabled={loading}
                        >
                          <CheckCircle className="mr-1 h-4 w-4" />
                          Auto-Fix
                        </Button>
                      </>
                    )}
                    
                    {alert.status === 'Reviewing' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleUpdateAlertStatus(alert.id, 'Ignored')}
                          disabled={loading}
                        >
                          <X className="mr-1 h-4 w-4" />
                          Ignore
                        </Button>
                        
                        <Button 
                          size="sm"
                          onClick={() => handleUpdateAlertStatus(alert.id, 'Resolved')}
                          disabled={loading}
                        >
                          <CheckCircle className="mr-1 h-4 w-4" />
                          Mark Resolved
                        </Button>
                      </>
                    )}
                  </div>
                </CardFooter>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                {loading ? (
                  <RefreshCw className="h-8 w-8 animate-spin mb-4" />
                ) : (
                  <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                )}
                <h3 className="text-xl font-semibold mb-2">
                  {loading ? 'Loading alerts...' : 'No Policy Alerts Found'}
                </h3>
                <p className="text-muted-foreground text-center max-w-md">
                  {loading 
                    ? 'Please wait while we check for policy compliance issues.'
                    : 'All admin policies appear to be following the standard implementation patterns. Great job!'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="documentation">
          <Card>
            <CardHeader>
              <CardTitle>Admin Check Implementation Standard</CardTitle>
              <CardDescription>
                Guidelines for implementing admin checks in the database
              </CardDescription>
            </CardHeader>
            
            <CardContent className="prose max-w-none">
              <h3>Key Components</h3>
              
              <p className="mb-4">
                <strong>Security Definer Function</strong>: The core of our admin check system is the <code>check_admin_status</code> function:
              </p>
              
              <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
{`CREATE OR REPLACE FUNCTION public.check_admin_status(input_user_id UUID)
RETURNS BOOLEAN
LANGUAGE plpgsql
SECURITY DEFINER
SET search_path = public
AS $$
BEGIN
  RETURN EXISTS (
    SELECT 1 
    FROM public.admin_users 
    WHERE user_id = input_user_id
  );
END;
$$;`}
              </pre>
              
              <p className="mb-4">
                <strong>Standard RLS Policy Pattern</strong>: All admin-based RLS policies should follow this pattern:
              </p>
              
              <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
{`CREATE POLICY "Policy name" 
  ON table_name 
  FOR operation
  USING (check_admin_status(auth.uid()));`}
              </pre>
              
              <h3>Important Guidelines</h3>
              
              <ul>
                <li>Never query the <code>admin_users</code> table directly from RLS policies</li>
                <li>Always use <code>check_admin_status</code> to check admin status</li>
                <li>All admin check functions must use <code>SECURITY DEFINER</code> with appropriate <code>search_path</code> settings</li>
                <li>For frontend components, always use the <code>useAdmin</code> React hook</li>
              </ul>
              
              <h3>Enforcement Mechanisms</h3>
              
              <p>
                The system includes automated enforcement mechanisms to ensure compliance:
              </p>
              
              <ol>
                <li><strong>Automated Policy Auditing</strong>: A scheduled job runs daily to scan all admin policies and verify they follow the standard pattern.</li>
                <li><strong>Policy Enforcement Trigger</strong>: A database trigger prevents the creation of non-compliant admin policies.</li>
                <li><strong>Auto-Fix Capability</strong>: Non-compliant policies can often be automatically fixed using the <code>fix_admin_policy()</code> function.</li>
                <li><strong>Alert System</strong>: Policy violations generate alerts in the admin dashboard for review.</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
